import {isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {UuidResponseBodySchema} from '@dms/api/shared';

import {
  ArchiveVehicleFinancingTemplateApiArg,
  ArchiveVehicleFinancingTemplateRequestBodySchema,
  CloneVehicleFinancingTemplateApiArg,
  CloneVehicleFinancingTemplateApiResponse,
  CreateVehicleFinancingTemplateApiArg,
  CreateVehicleFinancingTemplateApiResponse,
  CreateVehicleFinancingTemplateRequestBodySchema,
  DeleteVehicleFinancingTemplateApiArg,
  GetVehicleFinancingTemplateApiArg,
  ListVehicleFinancingTemplateApiResponse,
  ListVehicleFinancingTemplateApiResponseSchema,
  ReorderAutomaticVehicleFinancingTemplatesApiArg,
  ReorderAutomaticVehicleFinancingTemplatesRequestBodySchema,
  RestoreVehicleFinancingTemplateApiArg,
  UpdateVehicleFinancingTemplateApiArg,
  UpdateVehicleFinancingTemplateRequestBodySchema,
  VehicleFinancingTemplateResponseBody,
  VehicleFinancingTemplateResponseBodySchema,
} from './types';

/**
 * Tagging notes for this API:
 * - The template list is cached under the `VehicleFinancingTemplate` type with a reserved `LIST` id
 *   instead of its own type, so a bare `VehicleFinancingTemplate` invalidation covers the list and
 *   every template detail at once.
 * - The bare `SaleVehicleFinancing` invalidation is a deliberate fan-out: a template change alters
 *   the financing offered on every sale vehicle that resolves to it and those vehicle IDs are not
 *   known here. Leasing is never template-driven, so `SaleVehicleLeasing` is left untouched.
 */
export const vehicleFinancingTemplateApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    listVehicleFinancingTemplate: build.query<ListVehicleFinancingTemplateApiResponse, void>({
      query: () => ({url: '/dms/v1/vehicle-financing-template'}),
      providesTags: [{type: 'VehicleFinancingTemplate', id: 'LIST'}],
      extraOptions: {
        responseSchema: ListVehicleFinancingTemplateApiResponseSchema,
      },
    }),
    getVehicleFinancingTemplate: build.query<
      VehicleFinancingTemplateResponseBody,
      GetVehicleFinancingTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'VehicleFinancingTemplate', id: queryArg.vehicleFinancingTemplateId},
      ],
      extraOptions: {
        responseSchema: VehicleFinancingTemplateResponseBodySchema,
      },
    }),
    createVehicleFinancingTemplate: build.mutation<
      CreateVehicleFinancingTemplateApiResponse,
      CreateVehicleFinancingTemplateApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/vehicle-financing-template',
        method: 'POST',
        body: queryArg.createVehicleFinancingTemplateRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess([
        {type: 'VehicleFinancingTemplate', id: 'LIST'},
        'SaleVehicleFinancing',
      ]),
      extraOptions: {
        requestSchema: CreateVehicleFinancingTemplateRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    updateVehicleFinancingTemplate: build.mutation<void, UpdateVehicleFinancingTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}`,
        method: 'PUT',
        body: queryArg.updateVehicleFinancingTemplateRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: UpdateVehicleFinancingTemplateApiArg) => [
          {type: 'VehicleFinancingTemplate', id: queryArg.vehicleFinancingTemplateId},
          {type: 'VehicleFinancingTemplate', id: 'LIST'},
          'SaleVehicleFinancing',
        ]
      ),
      extraOptions: {
        requestSchema: UpdateVehicleFinancingTemplateRequestBodySchema,
      },
    }),
    archiveVehicleFinancingTemplate: build.mutation<void, ArchiveVehicleFinancingTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}/archive`,
        method: 'POST',
        body: queryArg.archiveVehicleFinancingTemplateRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: ArchiveVehicleFinancingTemplateApiArg) => [
          {type: 'VehicleFinancingTemplate', id: queryArg.vehicleFinancingTemplateId},
          {type: 'VehicleFinancingTemplate', id: 'LIST'},
          'SaleVehicleFinancing',
        ]
      ),
      extraOptions: {
        requestSchema: ArchiveVehicleFinancingTemplateRequestBodySchema,
      },
    }),
    restoreVehicleFinancingTemplate: build.mutation<void, RestoreVehicleFinancingTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}/restore`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: RestoreVehicleFinancingTemplateApiArg) => [
          {type: 'VehicleFinancingTemplate', id: queryArg.vehicleFinancingTemplateId},
          {type: 'VehicleFinancingTemplate', id: 'LIST'},
          'SaleVehicleFinancing',
        ]
      ),
    }),
    deleteVehicleFinancingTemplate: build.mutation<void, DeleteVehicleFinancingTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: DeleteVehicleFinancingTemplateApiArg) => [
          {type: 'VehicleFinancingTemplate', id: queryArg.vehicleFinancingTemplateId},
          {type: 'VehicleFinancingTemplate', id: 'LIST'},
          'SaleVehicleFinancing',
        ]
      ),
    }),
    // The `{id}` in the URL identifies the SOURCE template being cloned, which is not itself
    // mutated by cloning — only the list changes (a new template appears), so no detail tag is
    // invalidated here.
    cloneVehicleFinancingTemplate: build.mutation<
      CloneVehicleFinancingTemplateApiResponse,
      CloneVehicleFinancingTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle-financing-template/${encodeURIComponent(queryArg.vehicleFinancingTemplateId)}/clone`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess([
        {type: 'VehicleFinancingTemplate', id: 'LIST'},
        'SaleVehicleFinancing',
      ]),
      extraOptions: {
        responseSchema: UuidResponseBodySchema,
      },
    }),
    reorderAutomaticVehicleFinancingTemplates: build.mutation<
      void,
      ReorderAutomaticVehicleFinancingTemplatesApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/vehicle-financing-template/automatic-order',
        method: 'PUT',
        body: queryArg.reorderAutomaticVehicleFinancingTemplatesRequestBody,
      }),
      // The list is the single source of truth for the automatic order, and every consumer derives
      // the next drag from it. Without an optimistic patch, a second drag started before the
      // refetch lands is computed from the pre-drag order and silently reverts the first one.
      onQueryStarted: (
        {reorderAutomaticVehicleFinancingTemplatesRequestBody: requestBody},
        {dispatch, queryFulfilled}
      ) => {
        const patchResult = dispatch(
          vehicleFinancingTemplateApi.util.updateQueryData(
            'listVehicleFinancingTemplate',
            undefined,
            (draft) => {
              draft.forEach((template) => {
                const position = requestBody.positions.find(
                  (entry) => entry.templateId === template.id
                )?.position;

                if (isNotNil(position)) {
                  template.automaticPosition = position;
                }
              });
            }
          )
        );

        queryFulfilled.catch(patchResult.undo);
      },
      // The bare template tag covers every template detail *and* the `LIST` id, so the list tag is
      // not repeated here.
      invalidatesTags: invalidateOnSuccess(['VehicleFinancingTemplate', 'SaleVehicleFinancing']),
      extraOptions: {
        requestSchema: ReorderAutomaticVehicleFinancingTemplatesRequestBodySchema,
      },
    }),
  }),
});

export const {
  useListVehicleFinancingTemplateQuery,
  useGetVehicleFinancingTemplateQuery,
  useLazyGetVehicleFinancingTemplateQuery,
  useCreateVehicleFinancingTemplateMutation,
  useUpdateVehicleFinancingTemplateMutation,
  useArchiveVehicleFinancingTemplateMutation,
  useRestoreVehicleFinancingTemplateMutation,
  useDeleteVehicleFinancingTemplateMutation,
  useCloneVehicleFinancingTemplateMutation,
  useReorderAutomaticVehicleFinancingTemplatesMutation,
} = vehicleFinancingTemplateApi;
